import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { ProfileRepeatOrderButton } from '@eliadmin/core/src/components/profile/profile-repeat-order-button';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { deleteFromCart } from '@eliadmin/core/src/api/cart/cart-api';
import type {
  CustomerOrder,
  CustomOrderItem,
} from '@eliadmin/core/src/api/customers/customers-types';
import { ProfileOrderStatus } from '@eliadmin/core/src/components/profile/profile-order-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DialogTitle } from '@radix-ui/react-dialog';
import { DotIcon } from 'lucide-react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useState, type PropsWithChildren, type ReactNode } from 'react';

type ProfileOrderDialogProps = {
  order: CustomerOrder;
};

export function ProfileOrderDialog(props: PropsWithChildren<ProfileOrderDialogProps>) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const { data: cart } = useCart();

  const { addToCartMutation, deleteCartMutation } = useCartActions();

  const [isLoadingCopyOrder, setIsLoadingCopyOrder] = useState(false);
  const [isOpenCopyOrderDialog, setIsOpenCopyOrderDialog] = useState(false);
  const [productsWithError, setProductsWithError] = useState<CustomOrderItem[]>([]);

  const me = useCustomerStore(s => s.me);

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const copyOrder = async () => {
    setProductsWithError([]);
    setIsLoadingCopyOrder(true);

    // Delete items from the cart with a delay
    if (cart.items) {
      for (const item of cart.items) {
        await deleteCartMutation.mutateAsync({ hash: item.hash });
        await delay(100);
      }
    }

    // Add items to the cart with a delay
    for (const item of props.order.items) {
      try {
        await addToCartMutation.mutateAsync({
          locale,
          // TODO: implement quantity
          body: {
            item: {
              product: { id: item.product.id },
              sub_items: {
                services: [],
                packages: [],
              },
              ...(item.variant ? { variant: { id: item.variant.id } } : {}),
            },
            qty: item.quantity,
          },
        });
        await delay(100);
      } catch {
        setProductsWithError(prev => [...prev, item]);
      }
    }
    setIsLoadingCopyOrder(false);
    setIsOpenCopyOrderDialog(true);
  };

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>{props.children}</DialogTrigger>
        <DialogContent
          className={cn(
            'flex flex-col gap-4',
            isLoadingCopyOrder ? 'pointer-events-none cursor-not-allowed opacity-50' : '',
          )}
        >
          {/* header */}
          <DialogHeader>
            <DialogTitle className="text-left text-2xl font-bold">{t('text17')}</DialogTitle>
          </DialogHeader>
          <Separator />

          {/* image */}
          <div className="flex max-h-[45vh] flex-col gap-3 overflow-y-auto">
            {props.order.items.map((item, index) => (
              <div key={`order-item-${item.id ?? item.product?.id ?? item.name ?? index}`}>
                <div className="flex items-center gap-4">
                  <Image
                    src={item.image.url}
                    alt={resolveImageAlt(item?.image, undefined)}
                    width={0}
                    height={0}
                    sizes="(max-width: 100px) 100vw, 100px"
                    className={cn('h-full max-h-[100px] w-full max-w-[100px] object-contain')}
                  />

                  <div className="flex flex-col gap-4">
                    <h6 className="line-clamp-2 text-sm font-bold sm:text-lg">{item.name}</h6>

                    {/* TODO: attributy */}

                    <span className="text-sm font-bold sm:text-lg">
                      {item.quantity}x{' '}
                      {formatPrice(
                        roundPrice(item.price_with_vat || item.price),
                        currency,
                        currencySettings,
                      )}
                    </span>
                  </div>
                </div>
                {index < props.order.items.length - 1 && <Separator className="mt-2" />}
              </div>
            ))}
          </div>

          {/* order info */}
          <Separator />
          <div className="flex flex-col gap-4">
            <InfoAttribute label={t('text108')} value={props.order.order_identifier} />
            {!!props.order.order_created_at && (
              <InfoAttribute
                label={t('text393')}
                value={new Date(props.order.order_created_at).toLocaleString(locale)}
              />
            )}
            {!!props.order.created_at && (
              <InfoAttribute
                label={t('text716')}
                value={new Date(props.order.created_at).toLocaleString(locale)}
              />
            )}
            {!!(props.order.price_with_vat || props.order.price) && (
              <InfoAttribute
                label={t('text26')}
                value={formatPrice(
                  roundPrice(props.order.price_with_vat || props.order.price),
                  currency,
                  currencySettings,
                )}
              />
            )}
            <InfoAttribute
              label={t('text245')}
              value={<ProfileOrderStatus order={props.order} />}
            />
            <Button onClick={() => copyOrder()} disabled={isLoadingCopyOrder}>
              {isLoadingCopyOrder ? <Spinner /> : 'Opätovné odoslanie objednávky'}
            </Button>
          </div>
          <ProfileRepeatOrderButton orderId={props.order.id} className="w-full" />

          <OrderLoyaltyReward reward={props.order.loyalty_reward} refreshOrders />
        </DialogContent>
      </Dialog>
      <Dialog open={isOpenCopyOrderDialog} onOpenChange={setIsOpenCopyOrderDialog}>
        <DialogContent>
          {productsWithError.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-[16px] text-sm font-bold">
                Ospravedlňujeme sa, ale tieto produkty nebolo možné pridať do košíka
              </p>
              <ul className="text-sm text-destructive-foreground">
                {productsWithError.map(item => (
                  <li key={item.id} className="line-clamp-2 flex font-semibold">
                    <DotIcon className="h-5 w-5 min-w-5" />
                    {item.name}
                  </li>
                ))}
              </ul>
              <p className="text-[16px] text-sm font-bold">
                Dôvodom môže byť nedostupnosť produktu alebo neočakávaná chyba.
              </p>
              <p className="text-[16px] text-sm font-bold">Ostatné produkty boli pridané úspešne</p>
            </div>
          )}
          {productsWithError.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2">
              <p className="text-lg font-bold">Objednávka bola úspešne vložená do košíka.</p>
            </div>
          )}
          <Button asChild>
            <Link href={ROUTES.CART()}>{t('text456')}</Link>
          </Button>
          <ProfileRepeatOrderButton orderId={props.order.id} className="w-full" />
        </DialogContent>
      </Dialog>
    </>
  );
}

type InfoAttributeProps = {
  label: string;
  value: ReactNode;
};
function InfoAttribute(props: InfoAttributeProps) {
  return (
    <div className="flex flex-row justify-between">
      <span className="text-sm font-bold text-muted-foreground">{props.label}</span>
      <span className="text-sm font-normal">{props.value}</span>
    </div>
  );
}
